import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Paper, Typography, Button, Divider, CircularProgress } from "@material-ui/core";
import { createEventAttribute } from "../../../../../data-store/actions/events-actions";
import ParticipantSelector from "./participantSelectorShared";
import SheetFormatEditor from "./SheetFormatEditor";
import AttributePickerEditor from "./AttributePickerEditor";
import ValueRemapEditor from "./ValueRemapEditor";
import GridDesigner from "./GridDesigner";
import { defaultSheetFormat, validateSheetFormat } from "./sheetFormatLogic";
import { listarAtributosEtiquetables } from "./attributeLogic";
import { defaultGrid } from "./gridLogic";
import { generarEtiquetasPDF } from "./EtiquetasPDF";

// Contenedor del tab "Etiquetas": selecciona participantes, elige qué datos
// van en la etiqueta, diseña su grid interno, configura el formato de la
// hoja de impresión y genera el PDF.
export default function EtiquetasConfigContainer() {
  const dispatch = useDispatch();
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const eventSubEventsRegistryState = useSelector((state) => state.events.eventSubEventsRegistry);
  const eventParticipantsState = useSelector((state) => state.registry.eventParticipants);
  const registryConfiguration = useSelector((state) => state.registry.registryConfiguration);
  const eventoIndex = "evento_" + eventDetail.iDEvento;

  const [sheetFormat, setSheetFormat] = useState(defaultSheetFormat());
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [valueRemap, setValueRemap] = useState({});
  const [grid, setGrid] = useState(defaultGrid);
  const [participantesSeleccionados, setParticipantesSeleccionados] = useState([]);
  const [mensajeGuardado, setMensajeGuardado] = useState("");
  const [mensajeGuardadoAtributos, setMensajeGuardadoAtributos] = useState("");
  const [mensajeGuardadoGrid, setMensajeGuardadoGrid] = useState("");
  const [generando, setGenerando] = useState(false);
  const [resultadoPdf, setResultadoPdf] = useState(null);
  const inicializadoRef = useRef(false);

  useEffect(() => {
    if (inicializadoRef.current) return;
    if (registryConfiguration === undefined) return; // sigue cargando desde Firebase
    const etiquetasGuardadas = registryConfiguration?.etiquetasConfiguracion;
    if (etiquetasGuardadas?.sheetFormat) {
      setSheetFormat({ ...defaultSheetFormat(), ...etiquetasGuardadas.sheetFormat });
    }
    if (Array.isArray(etiquetasGuardadas?.selectedAttributes)) {
      setSelectedAttributes(etiquetasGuardadas.selectedAttributes);
    }
    if (etiquetasGuardadas?.valueRemap) {
      setValueRemap(etiquetasGuardadas.valueRemap);
    }
    if (etiquetasGuardadas?.grid?.columns?.length > 0) {
      setGrid(etiquetasGuardadas.grid);
    }
    inicializadoRef.current = true;
  }, [registryConfiguration]);

  const atributosDisponibles = useMemo(
    () => listarAtributosEtiquetables(registryConfiguration),
    [registryConfiguration]
  );

  const todosLosParticipantes = useMemo(
    () => Object.values(eventParticipantsState || {}),
    [eventParticipantsState]
  );

  // Participante de ejemplo para la vista previa en vivo del diseño: usa el
  // primero de la lista real si ya hay datos cargados, o un placeholder si
  // el evento todavía no tiene inscritos.
  const participanteEjemplo = useMemo(() => {
    if (todosLosParticipantes.length > 0) return todosLosParticipantes[0];
    return { nombres: "Juan", apellidos: "Pérez", numero: 123, modalidad: "", rama: "V", categoria: "" };
  }, [todosLosParticipantes]);

  const erroresFormato = validateSheetFormat(sheetFormat);
  const formatoValido = Object.keys(erroresFormato).length === 0;

  function guardarFormato() {
    dispatch(
      createEventAttribute(
        eventoIndex,
        "inscripcionesConfiguracion/etiquetasConfiguracion/sheetFormat",
        sheetFormat
      )
    ).finally(() => {
      setMensajeGuardado("Formato guardado. La próxima vez que abras este tab se precargará.");
    });
  }

  function guardarAtributosYRemap() {
    Promise.all([
      dispatch(
        createEventAttribute(
          eventoIndex,
          "inscripcionesConfiguracion/etiquetasConfiguracion/selectedAttributes",
          selectedAttributes
        )
      ),
      dispatch(
        createEventAttribute(
          eventoIndex,
          "inscripcionesConfiguracion/etiquetasConfiguracion/valueRemap",
          valueRemap
        )
      ),
    ]).finally(() => {
      setMensajeGuardadoAtributos(
        "Datos de la etiqueta guardados. La próxima vez que abras este tab se precargarán."
      );
    });
  }

  function guardarGrid() {
    dispatch(
      createEventAttribute(eventoIndex, "inscripcionesConfiguracion/etiquetasConfiguracion/grid", grid)
    ).finally(() => {
      setMensajeGuardadoGrid("Diseño guardado. La próxima vez que abras este tab se precargará.");
    });
  }

  function restaurarGridPorDefecto() {
    setGrid(defaultGrid());
    setMensajeGuardadoGrid("");
  }

  async function generarPDF() {
    setGenerando(true);
    try {
      const resultado = await generarEtiquetasPDF({
        participantesSeleccionados,
        config: sheetFormat,
        eventSubEventsRegistry: eventSubEventsRegistryState,
        valueRemap,
        atributosDisponibles,
        grid,
      });
      setResultadoPdf(resultado);
    } finally {
      setGenerando(false);
    }
  }

  const puedeGenerar = formatoValido && participantesSeleccionados.length > 0 && !generando;

  return (
    <Box mt={2}>
      <Paper style={{ padding: 20, marginBottom: 20 }}>
        <Typography variant="h6" gutterBottom>
          1. Selecciona a quién imprimir la etiqueta
        </Typography>
        <ParticipantSelector
          eventoIndex={eventoIndex}
          onSelectionChange={setParticipantesSeleccionados}
        />
      </Paper>

      <Paper style={{ padding: 20, marginBottom: 20 }}>
        <Typography variant="h6" gutterBottom>
          2. Elige los datos de la etiqueta
        </Typography>
        <AttributePickerEditor
          atributosDisponibles={atributosDisponibles}
          selectedAttributes={selectedAttributes}
          onChange={setSelectedAttributes}
        />
        <Box mt={2}>
          <ValueRemapEditor
            selectedAttributes={selectedAttributes}
            atributosDisponibles={atributosDisponibles}
            participantesFiltrados={todosLosParticipantes}
            eventSubEventsRegistry={eventSubEventsRegistryState}
            valueRemap={valueRemap}
            onChange={setValueRemap}
          />
        </Box>
        <Box mt={2} display="flex" style={{ gap: 12 }} alignItems="center">
          <Button variant="outlined" onClick={guardarAtributosYRemap}>
            Guardar datos de la etiqueta
          </Button>
          {mensajeGuardadoAtributos && (
            <Typography variant="body2" style={{ opacity: 0.75 }}>
              {mensajeGuardadoAtributos}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper style={{ padding: 20, marginBottom: 20 }}>
        <Typography variant="h6" gutterBottom>
          3. Diseña la etiqueta
        </Typography>
        <GridDesigner
          grid={grid}
          onChange={setGrid}
          selectedAttributes={selectedAttributes}
          atributosDisponibles={atributosDisponibles}
          eventoIndex={eventDetail.iDEvento}
          participanteEjemplo={participanteEjemplo}
          eventSubEventsRegistry={eventSubEventsRegistryState}
          valueRemap={valueRemap}
          sheetFormat={sheetFormat}
        />
        <Box mt={2} display="flex" style={{ gap: 12 }} alignItems="center">
          <Button variant="outlined" onClick={guardarGrid}>
            Guardar diseño
          </Button>
          <Button variant="text" onClick={restaurarGridPorDefecto}>
            Restaurar diseño por defecto
          </Button>
          {mensajeGuardadoGrid && (
            <Typography variant="body2" style={{ opacity: 0.75 }}>
              {mensajeGuardadoGrid}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper style={{ padding: 20, marginBottom: 20 }}>
        <Typography variant="h6" gutterBottom>
          4. Formato de la hoja de impresión
        </Typography>
        <SheetFormatEditor value={sheetFormat} onChange={setSheetFormat} />
        <Box mt={2} display="flex" style={{ gap: 12 }} alignItems="center">
          <Button variant="outlined" onClick={guardarFormato} disabled={!formatoValido}>
            Guardar formato
          </Button>
          {mensajeGuardado && (
            <Typography variant="body2" style={{ opacity: 0.75 }}>
              {mensajeGuardado}
            </Typography>
          )}
        </Box>
      </Paper>

      <Divider style={{ margin: "20px 0" }} />

      <Box display="flex" style={{ gap: 12 }} alignItems="center" flexWrap="wrap">
        <Button
          variant="contained"
          color="primary"
          onClick={generarPDF}
          disabled={!puedeGenerar}
          startIcon={generando ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {generando
            ? "Generando..."
            : `Generar PDF (${participantesSeleccionados.length} seleccionados)`}
        </Button>
        {!formatoValido && (
          <Typography variant="body2" color="error">
            Corrige el formato de hoja antes de generar el PDF.
          </Typography>
        )}
      </Box>

      {resultadoPdf && (
        <Box mt={2}>
          <Typography variant="body2" style={{ fontWeight: "bold" }}>
            {resultadoPdf.generados} etiqueta(s) generada(s).
          </Typography>
          {resultadoPdf.omitidos.length > 0 && (
            <Box mt={1}>
              <Typography variant="body2" color="error">
                {resultadoPdf.omitidos.length} inscrito(s) omitido(s) por no tener número de
                dorsal asignado:
              </Typography>
              {resultadoPdf.omitidos.map((o, i) => (
                <Typography variant="body2" key={i}>
                  - {o.nombres} {o.apellidos}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
